import PptxGenJS from 'pptxgenjs';
import IConnection from '@app/types/data/base/IConnection';
import { TextWithDimensions } from '@app/services/export/ppt/classes/TextWithDimensions';
import { colors } from '@app/services/export/ppt/variables/constants';
import { copy, h3 } from '@app/services/export/ppt/variables/typography';
import ConnectionType from '@app/types/base/ConnectionType';

export const connectionIconMap: Record<string, string> = {
	[ConnectionType.OneWay]: 'assets/images/icons/svg/direction-oneway.svg',
	[ConnectionType.RoundTrip]: 'assets/images/icons/svg/direction-roundtrip.svg',
	[ConnectionType.AnyDirection]: 'assets/images/icons/svg/direction-any.svg',
	[ConnectionType.OneWayOrRoundTrip]: 'assets/images/icons/svg/direction-oneway-or-roundtrip.svg'
};

interface IProps {
	connection: IConnection;
	variant?: 'default' | 'big';
	maxWidth: number;
}

interface IHelperDimensions {
	w: number; // inch
	h: number; // inch
}

export class Connection {
	protected slide: PptxGenJS.Slide;

	protected props: IProps;

	protected titlesWithDimension: TextWithDimensions[];

	protected iconWidth: number;

	protected readonly rowGap = 0;

	public constructor(slide: PptxGenJS.Slide, props: IProps) {
		this.slide = slide;
		this.props = props;
		this.titlesWithDimension = this.generateTitlesWithDimensions();
		this.iconWidth = this.props.variant === 'big' ? 0.4 : 0.2;
	}

	public createAtCoordinates(x: number, y: number): void {
		let currX = x;
		let currY = y;

		this.titlesWithDimension.forEach((item, index) => {
			if (currX + item.getDimensions().w > x + this.props.maxWidth) {
				currX = x;
				currY += item.getDimensions().h + this.rowGap;
			}

			item.createForSlide(this.slide, { x: currX, y: currY });

			if (index !== this.props.connection.stations.length - 1) {
				this.slide.addImage({
					path: connectionIconMap[this.props.connection.type],
					y,
					x: currX + item.getDimensions().w,
					w: 15,
					h: 18,
					sizing: {
						type: 'contain',
						w: this.iconWidth,
						h: item.getDimensions().h
					}
				});
				currX += item.getDimensions().w + this.iconWidth;
			}
		});
	}

	protected generateTitlesWithDimensions(): TextWithDimensions[] {
		const isBig = this.props.variant === 'big';

		return this.props.connection.stations.map(
			station =>
				new TextWithDimensions({
					text: station.title,
					textOptions: { ...(isBig ? h3 : copy), color: colors.blue }
				})
		);
	}

	public getDimensions(): IHelperDimensions {
		const rows: [number, number][] = []; // [w,h]
		let currW = 0;
		let currH = 0;

		this.titlesWithDimension.forEach((item, index) => {
			if (currW + item.getDimensions().w > this.props.maxWidth) {
				rows.push([currW, currH]);
				currW = 0;
				currH = 0;
			}

			currW += item.getDimensions().w;
			currH = item.getDimensions().h + (index > 0 ? this.rowGap : 0);

			if (index !== this.props.connection.stations.length - 1) {
				currW += this.iconWidth;
			}

			if (index === this.props.connection.stations.length - 1) {
				rows.push([currW, currH]);
			}
		});

		const maxW = Math.max(...rows.map(item => item[0]));
		const sumH = rows.reduce((acc, item) => acc + item[1], 0);

		return { w: maxW, h: sumH };
	}
}
